import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { api, ApiError } from '../../lib/api';
import type { RolesOverview, RolesOverviewMember } from '../../lib/types';

function memberLabel(m: RolesOverviewMember): string {
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return full || m.email || '—';
}

export function OrganizationRolesModal({ isOrgAdmin, onClose }: { isOrgAdmin: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const [overview, setOverview] = useState<RolesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Роль меняется локально до нажатия «Сохранить» — key = "userId:workspaceId".
  const [pending, setPending] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setOverview(await api.getRolesOverview());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить роли.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const roleName = (code: string) => overview?.roles.find((r) => r.code === code)?.name ?? code;
  const pendingCount = Object.keys(pending).length;
  const cellKey = (userId: string, workspaceId: string) => `${userId}:${workspaceId}`;

  const handleCellChange = (userId: string, workspaceId: string, currentRole: string, newRole: string) => {
    const key = cellKey(userId, workspaceId);
    setPending((prev) => {
      const next = { ...prev };
      if (newRole === currentRole) delete next[key];
      else next[key] = newRole;
      return next;
    });
  };

  const handleDiscard = () => setPending({});

  const handleSaveAll = async () => {
    if (!overview) return;
    const changedCount = Object.keys(pending).length;
    setSaving(true);
    setError(null);
    try {
      for (const key of Object.keys(pending)) {
        const [userId, workspaceId] = key.split(':');
        const newRole = pending[key];
        const member = overview.members.find((m) => m.user_id === userId);
        const hadAssignment = Boolean(
          member?.assignments.find((a) => a.workspace_id === workspaceId && a.role_codes.length > 0),
        );
        if (!newRole) {
          if (hadAssignment) await api.removeWorkspaceMember(workspaceId, userId);
        } else if (hadAssignment) {
          await api.updateWorkspaceMemberRole(workspaceId, userId, newRole);
        } else {
          await api.assignWorkspaceMember(workspaceId, userId, newRole);
        }
      }
      setPending({});
      await load();
      showToast('success', `Роли сохранены (изменений: ${changedCount})`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Не удалось сохранить изменения.';
      setError(message);
      showToast('error', message);
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!overview) return [];
    const q = search.trim().toLowerCase();
    if (!q) return overview.members;
    return overview.members.filter((m) => memberLabel(m).toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q));
  }, [overview, search]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/52 p-4">
      <div className="flex max-h-[85vh] w-full max-w-[1100px] flex-col overflow-hidden rounded-[18px] border border-border-default bg-bg-surface p-6">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-base font-semibold text-text-primary">Роли участников по пространствам</h2>
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <>
                <span className="text-[11px] text-text-secondary">Изменений: {pendingCount}</span>
                <button
                  type="button"
                  onClick={handleDiscard}
                  disabled={saving}
                  className="text-[11px] font-semibold text-text-secondary hover:text-text-primary"
                >
                  Отменить
                </button>
                <Button type="button" className="h-8 px-4 text-[11px]" onClick={handleSaveAll} disabled={saving}>
                  {saving ? 'Сохраняем…' : 'Сохранить'}
                </Button>
              </>
            )}
            <button type="button" onClick={onClose} aria-label="Закрыть" className="text-lg font-semibold text-text-secondary hover:text-text-primary">
              ×
            </button>
          </div>
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по имени или почте…"
          className="mt-4 h-10 w-full max-w-[320px] rounded-[9px] border border-border-default bg-bg-surface px-3 text-[13px] text-text-primary outline-none focus:border-action-primary"
        />

        {error && <p className="mt-3 text-xs font-medium text-status-warning">{error}</p>}

        <div className="mt-4 flex-1 overflow-auto">
          {!overview ? (
            <p className="text-xs text-text-secondary">Загрузка…</p>
          ) : overview.workspaces.length === 0 ? (
            <p className="text-xs text-text-secondary">В организации пока нет пространств.</p>
          ) : (
            <table className="w-full min-w-[760px] border-collapse text-[12px]">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 bg-bg-surface px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">
                    Участник
                  </th>
                  <th className="sticky top-0 bg-bg-surface px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">Почта</th>
                  {overview.workspaces.map((ws) => (
                    <th key={ws.id} className="sticky top-0 bg-bg-surface px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">
                      {ws.name}
                    </th>
                  ))}
                  <th className="sticky top-0 bg-bg-surface px-3 py-2 text-left text-[10px] font-semibold text-text-secondary">Группы</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {filteredMembers.length === 0 && (
                  <tr>
                    <td colSpan={overview.workspaces.length + 3} className="py-6 text-center text-xs text-text-secondary">
                      Никого не найдено
                    </td>
                  </tr>
                )}
                {filteredMembers.map((m) => (
                  <tr key={m.user_id}>
                    <td className="sticky left-0 bg-bg-surface px-3 py-2.5">
                      <p className="text-xs font-semibold text-text-primary">{memberLabel(m)}</p>
                      {m.org_is_admin && (
                        <span className="mt-0.5 inline-block text-[9px] font-medium text-action-primary">
                          Администратор организации
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-[11px] text-text-secondary">{m.email ?? '—'}</td>
                    {overview.workspaces.map((ws) => {
                      const assignment = m.assignments.find((a) => a.workspace_id === ws.id);
                      const directRole = assignment?.role_codes[0] ?? '';
                      const key = cellKey(m.user_id, ws.id);
                      const displayRole = key in pending ? pending[key] : directRole;
                      const isDirty = key in pending;
                      const groupRoleCodes = Array.from(
                        new Set(
                          m.group_ids.flatMap((gid) => {
                            const group = overview.groups.find((g) => g.id === gid);
                            return group?.grants.filter((g) => g.workspace_id === ws.id).map((g) => g.role_code) ?? [];
                          }),
                        ),
                      );

                      return (
                        <td key={ws.id} className="px-3 py-2.5">
                          {isOrgAdmin ? (
                            <select
                              value={displayRole}
                              onChange={(e) => handleCellChange(m.user_id, ws.id, directRole, e.target.value)}
                              className={`h-8 rounded-md border bg-bg-surface px-2 text-[11px] text-text-primary ${
                                isDirty ? 'border-action-primary ring-1 ring-action-primary' : 'border-border-default'
                              }`}
                            >
                              <option value="">—</option>
                              {overview.roles.map((r) => (
                                <option key={r.code} value={r.code}>
                                  {r.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-[11px] text-text-primary">{directRole ? roleName(directRole) : '—'}</span>
                          )}
                          {groupRoleCodes.length > 0 && (
                            <p className="mt-1 text-[9px] text-text-secondary">
                              через группу: {groupRoleCodes.map(roleName).join(', ')}
                            </p>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {m.group_ids.map((gid) => {
                          const group = overview.groups.find((g) => g.id === gid);
                          return (
                            <span
                              key={gid}
                              className="inline-flex items-center gap-1 rounded-full bg-bg-surface-secondary px-2 py-0.5 text-[10px] font-medium text-text-primary"
                            >
                              {group?.name ?? gid}
                            </span>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
